import React from 'react';
import { Box, IconButton } from '@mui/material';
import { EIconTypes } from 'constants/Icons';
import Icon from 'components/atoms/Icon/Icon';
import { IComparisonSection, EComparisonCategory } from '../helpers';
import {
  MobileCategoryCard,
  MobileCategoryHeader,
  MobileCategoryTitle,
  MobileFieldCard,
  MobileFieldLabel,
  MobileComparisonRow,
  MobileComparisonValue,
} from './styles';

interface IMobileComparisonViewProps {
  comparisonSections: IComparisonSection[];
  formatValue: (value: any, row: any) => string | React.ReactNode;
  t: (key: string) => string;
  weaponLicenceCategory: any[];
  getDictionaryValue: (uniqueId: string, dictionary: any[]) => string;
  hasMultiple: boolean;
  additionalInfo: any;
  weaponDetailOpen: string;
  setWeaponDetailOpen: (arg: string) => void;
  attachmentDetailOpen: string;
  setAttachmentDetailOpen: (arg: string) => void;
}

export const MobileComparisonView = ({
  comparisonSections,
  formatValue,
  t,
  weaponLicenceCategory,
  getDictionaryValue,
  hasMultiple,
  additionalInfo,
  weaponDetailOpen,
  setWeaponDetailOpen,
  attachmentDetailOpen,
  setAttachmentDetailOpen,
}: IMobileComparisonViewProps) => (
  <Box>
    {comparisonSections.map((section) => {
      const hasChanges = section.rows.length > 0;
      let sectionTitle = t(section.categoryTranslationKey);
      let showCategoryHeaderFirstCell = true;
      let showSortIcon = false;

      if (section.categoryName.includes(EComparisonCategory.Weapon)) {
        showSortIcon = true;
        if (section.weaponIndex !== undefined && section.weaponIndex > 0) {
          showCategoryHeaderFirstCell = false;
        }

        if (section.weaponCategory) {
          const categoryText = getDictionaryValue(
            section.weaponCategory,
            weaponLicenceCategory || [],
          );
          if (categoryText) {
            sectionTitle = `${sectionTitle} (${categoryText})`;
          }
        }
      }
      if (section.categoryName.includes(EComparisonCategory.Attachments)) {
        showSortIcon = true;
        if (section.weaponIndex !== undefined && section.weaponIndex > 0) {
          showCategoryHeaderFirstCell = false;
        }
      }

      return (
        <MobileCategoryCard key={section.categoryName}>
          {!!showCategoryHeaderFirstCell && (
            <MobileCategoryHeader>
              <MobileCategoryTitle>{sectionTitle}</MobileCategoryTitle>
            </MobileCategoryHeader>
          )}
          {!!showCategoryHeaderFirstCell && !hasChanges && (
            <MobileComparisonRow>
              <MobileComparisonValue>
                {t('legalEntity.licenceDetails.history.noChanges')}
              </MobileComparisonValue>
              <MobileComparisonValue>
                {t('legalEntity.licenceDetails.history.noChanges')}
              </MobileComparisonValue>
            </MobileComparisonRow>
          )}
          {section.additionalRow && !!showCategoryHeaderFirstCell && hasMultiple && (
            <MobileFieldCard key={`${section.categoryName}-${section.additionalRow.name}`}>
              <MobileFieldLabel>
                {additionalInfo?.name ? t(additionalInfo?.name) : ''}
              </MobileFieldLabel>
              <MobileComparisonRow>
                <MobileComparisonValue>{additionalInfo?.before}</MobileComparisonValue>
                <MobileComparisonValue>{additionalInfo?.after}</MobileComparisonValue>
              </MobileComparisonRow>
            </MobileFieldCard>
          )}
          {hasChanges &&
            section.rows.map((row, rowIndex) => {
              const isFirstRowInSection = rowIndex === 0;
              const rowKey = section.rows[0].beforeValue || section.rows[0].afterValue;

              if (
                section.categoryName.includes(EComparisonCategory.Weapon) &&
                !isFirstRowInSection &&
                hasMultiple &&
                weaponDetailOpen !== rowKey
              ) {
                return null;
              }
              if (
                section.categoryName.includes(EComparisonCategory.Attachments) &&
                !isFirstRowInSection &&
                hasMultiple &&
                attachmentDetailOpen !== rowKey
              ) {
                return null;
              }

              const isWeaponSection = section.categoryName.includes(
                EComparisonCategory.Weapon,
              );
              const isAttachmentSection = section.categoryName.includes(
                EComparisonCategory.Attachments,
              );

              const handleSortButton = () => {
                if (isWeaponSection) {
                  setWeaponDetailOpen(weaponDetailOpen === rowKey ? '' : rowKey);
                }
                if (isAttachmentSection) {
                  setAttachmentDetailOpen(attachmentDetailOpen === rowKey ? '' : rowKey);
                }
              };
              return (
                <MobileFieldCard key={`${section.categoryName}-${row.translationKey}`}>
                  {!isFirstRowInSection && hasMultiple && showSortIcon ? (
                    ''
                  ) : (
                    <MobileFieldLabel>
                      {isFirstRowInSection && hasMultiple && showSortIcon ? (
                        <IconButton
                          onClick={() => handleSortButton()}
                          style={{
                            height: '19px',
                            width: '19px',
                            marginBottom: '3px',
                            marginRight: '5px',
                          }}
                        >
                          <Icon
                            type={
                              (isWeaponSection && weaponDetailOpen === rowKey) ||
                              (isAttachmentSection && attachmentDetailOpen === rowKey)
                                ? EIconTypes.sortDown
                                : EIconTypes.sortRight
                            }
                          />
                        </IconButton>
                      ) : (
                        ''
                      )}
                      {t(row.translationKey)}
                    </MobileFieldLabel>
                  )}
                  <MobileComparisonRow>
                    <MobileComparisonValue>
                      {formatValue(row.beforeValue, row)}
                    </MobileComparisonValue>
                    <MobileComparisonValue>
                      {formatValue(row.afterValue, row)}
                      {isFirstRowInSection && section.isNew && (
                        <span>
                          {`(${t('legalEntity.licenceDetails.linkedWeapons.new.label')})`}
                        </span>
                      )}
                      {isFirstRowInSection && section.isRemoved && (
                        <>
                          {formatValue(row.beforeValue, row)}
                          <span>
                            {`(${t(
                              'legalEntity.licenceDetails.linkedWeapons.removed.label',
                            )})`}
                          </span>
                        </>
                      )}
                    </MobileComparisonValue>
                  </MobileComparisonRow>
                </MobileFieldCard>
              );
            })}
        </MobileCategoryCard>
      );
    })}
  </Box>
);
